import { createStyles } from "@mantine/core";
import { tokens, Link, Tooltip } from "@blurb/codex-react";
import React from "react";
import { CopyableRow } from "../CopyableRow";
import { useRouterLinkProps } from "../../hooks/useRouterLinkProps";

const useStyles = createStyles({
  id: {
    display: "block",
    maxWidth: "100%",
    fontFamily: tokens.font.family.mono,
    // Codex's own component reset forces font-size/line-height on anything carrying
    // data-codex-component (which Link sets) — override back to inherit so the Link matches the
    // table row's font size instead of jumping to codex's base size.
    fontSize: "inherit",
    lineHeight: "inherit",
    "&[data-truncate-start]": {
      direction: "rtl",
      textAlign: "left",
    },
  },
});

interface CellIdProps {
  value: string;
  /** When provided, the id renders as a link to this path. */
  to?: string;
  /** Lead with an ellipsis and keep the suffix visible — for opaque ids (ULIDs) whose leading chars don't distinguish them. */
  truncateStart?: boolean;
}

interface CellIdLinkProps {
  to: string;
  value: string;
  className: string;
  truncateStart?: boolean;
  children: React.ReactNode;
}

const CellIdLink: React.FC<CellIdLinkProps> = ({ to, value, className, truncateStart, children }) => {
  const { href, onClick } = useRouterLinkProps(to);
  return (
    <Tooltip content={value}>
      <Link
        href={href}
        onClick={onClick}
        aria-label={value}
        data-truncate-start={truncateStart || undefined}
        className={className}
      >
        {children}
      </Link>
    </Tooltip>
  );
};

/** Table cell for a long identifier, truncated to fit (width-adaptive), with the full value via tooltip and copy. */
export const CellId: React.FC<CellIdProps> = ({ value, to, truncateStart }) => {
  const { classes, cx } = useStyles();

  // truncateStart flips truncation to the front with direction:rtl, so the CSS ellipsis lands on the
  // left and the distinguishing suffix stays visible as the column resizes. <bdi> isolates the value so
  // the rtl context doesn't reorder its (digit-heavy) characters.
  const inner = truncateStart ? <bdi>{value}</bdi> : value;
  const className = cx(classes.id, "text-ellipsis");

  return (
    <CopyableRow value={value} overflow="none" size="base">
      {to ? (
        <CellIdLink to={to} value={value} className={className} truncateStart={truncateStart}>
          {inner}
        </CellIdLink>
      ) : (
        <Tooltip content={value}>
          <span className={className} data-truncate-start={truncateStart || undefined}>
            {inner}
          </span>
        </Tooltip>
      )}
    </CopyableRow>
  );
};
